import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, logout } from '../lib/authFetch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, LogOut, Save, Download, FileText, ShieldCheck, History, Star } from 'lucide-react';

const FIELD_LABELS = {
  vendor: [
    ['ref', 'Reference'],
    ['organization', 'Organization'],
    ['org_type', 'Organization Type'],
    ['contact_name', 'Contact Person'],
    ['title', 'Title'],
    ['department', 'Department'],
    ['email', 'Email'],
    ['phone', 'Phone'],
    ['interest', 'Service Interest'],
    ['volume', 'Estimated Trip Volume'],
    ['transport_type', 'Transport Type'],
    ['message', 'Message'],
    ['source_page', 'Source Page'],
    ['created_at', 'Submitted'],
  ],
  job: [
    ['ref', 'Reference'],
    ['full_name', 'Applicant'],
    ['position', 'Position Applied For'],
    ['email', 'Email'],
    ['phone', 'Phone'],
    ['years_experience', 'Years of Experience'],
    ['availability', 'Availability'],
    ['cpr_certified', 'CPR Certified'],
    ['wheelchair_experience', 'Wheelchair Securement Experience'],
    ['background_consent', 'Background-Check Consent'],
    ['additional_info', 'Additional Info'],
    ['created_at', 'Submitted'],
  ],
  contact: [
    ['ref', 'Reference'],
    ['name', 'Name'],
    ['email', 'Email'],
    ['phone', 'Phone'],
    ['subject_label', 'Subject'],
    ['message', 'Message'],
    ['source_page', 'Source Page'],
    ['created_at', 'Submitted'],
  ],
  patient: [
    ['ref', 'Reference'],
    ['requesting_organization', 'Requesting Org'],
    ['requestor_name', 'Requestor'],
    ['requestor_title', 'Requestor Title'],
    ['email', 'Email'],
    ['phone', 'Phone'],
    ['patient_name', 'Patient (PHI — handle with care)'],
    ['pickup_address', 'Pickup'],
    ['destination', 'Destination'],
    ['appointment_date', 'Appointment Date'],
    ['appointment_time', 'Appointment Time'],
    ['transport_type', 'Transport Type'],
    ['trip_type', 'Trip Type'],
    ['oxygen_required', 'Oxygen Required'],
    ['claim_authorization', 'Claim Auth'],
    ['special_instructions', 'Special Instructions'],
    ['created_at', 'Submitted'],
  ],
  referral: [
    ['ref', 'Reference'],
    ['organization', 'Organization'],
    ['contact_name', 'Contact'],
    ['title', 'Title'],
    ['email', 'Email'],
    ['phone', 'Phone'],
    ['org_type', 'Org Type'],
    ['estimated_needs', 'Estimated Needs'],
    ['transport_type', 'Transport Type'],
    ['recurring_needed', 'Recurring Needed'],
    ['service_area', 'Service Area'],
    ['message', 'Message'],
    ['created_at', 'Submitted'],
  ],
};

function renderValue(val) {
  if (val === null || val === undefined || val === '') return <span className="text-gray-400">—</span>;
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  if (typeof val === 'object') return <code className="text-xs">{JSON.stringify(val)}</code>;
  return String(val);
}

const DispatchInquiryDetail = () => {
  const { kind, id } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draftStatus, setDraftStatus] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [savingRating, setSavingRating] = useState(false);

  const saveRating = async (value) => {
    if (savingRating) return;
    setSavingRating(true);
    try {
      const res = await authFetch(`/api/dispatch/inquiries/job/${id}/rating`, {
        method: 'PATCH',
        body: JSON.stringify({ rating: value }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Rating update failed.'); return; }
      toast.success(value ? `Rated ${value}/5` : 'Rating cleared');
      await load();
    } catch { toast.error('Network error.'); }
    finally { setSavingRating(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [detRes, auditRes] = await Promise.all([
        authFetch(`/api/dispatch/inquiries/${kind}/${id}`),
        authFetch(`/api/dispatch/inquiries/${kind}/${id}/audit-logs`),
      ]);
      if (!detRes.ok) {
        setError(detRes.status === 404 ? 'Record not found.' : `Unable to load record (HTTP ${detRes.status}).`);
        return;
      }
      const data = await detRes.json();
      setRecord(data.item);
      setStatusOptions(data.status_options || []);
      setDraftStatus(data.item.status || 'New');
      setNotes(data.item.internal_notes || '');
      if (auditRes.ok) setAudit((await auditRes.json()).logs || []);
    } catch (e) {
      setError('Network error while loading record.');
    } finally {
      setLoading(false);
    }
  }, [kind, id]);

  useEffect(() => { load(); }, [load]);

  const statusChanged = record && draftStatus !== record.status;
  const notesChanged = record && (notes || '') !== (record.internal_notes || '');
  const dirty = statusChanged || notesChanged;

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      if (statusChanged) {
        const res = await authFetch(`/api/dispatch/inquiries/${kind}/${id}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: draftStatus, reason: reason || undefined }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { toast.error(data.detail || 'Status update failed.'); setSaving(false); return; }
      }
      if (notesChanged) {
        const res = await authFetch(`/api/dispatch/inquiries/${kind}/${id}/notes`, {
          method: 'PATCH',
          body: JSON.stringify({ internal_notes: notes }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) { toast.error(data.detail || 'Notes update failed.'); setSaving(false); return; }
      }
      toast.success('Saved.');
      setReason('');
      await load();
    } catch (e) {
      toast.error('Network error.');
    } finally {
      setSaving(false);
    }
  };

  const downloadAttachment = async (slot) => {
    setDownloading(slot);
    try {
      const res = await authFetch(`/api/dispatch/inquiries/job/${id}/attachments/${slot}`);
      if (!res.ok) {
        toast.error(res.status === 404 ? 'Attachment missing.' : 'Download failed.');
        return;
      }
      const blob = await res.blob();
      const disp = res.headers.get('content-disposition') || '';
      const m = /filename="?([^"]+)"?/i.exec(disp);
      const name = (m && m[1]) || `${slot}.bin`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  };

  const fields = FIELD_LABELS[kind] || [];

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center" data-testid="detail-loading">
        <Loader2 className="mx-auto w-8 h-8 animate-spin text-[#004C97]" />
      </div>
    </div>
  );

  if (error || !record) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-6 text-center" data-testid="detail-error">
          <p className="text-sm text-red-800">{error || 'Record unavailable.'}</p>
          <Button onClick={() => navigate(-1)} className="mt-4">Back</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <header className="bg-[#004C97] shadow-sm sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" onClick={() => navigate(`/dispatch/inquiries/${kind}`)} className="text-white hover:bg-white/10 min-h-[44px] px-2">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-white text-base sm:text-lg font-semibold truncate">
                {record.ref} · {(kind || '').charAt(0).toUpperCase() + kind.slice(1)}
              </h1>
              <p className="text-[11px] text-white/70">Updated {record.updated_at ? new Date(record.updated_at).toLocaleString() : '—'}</p>
            </div>
          </div>
          <Button
            onClick={async () => { await logout(); }}
            variant="ghost"
            className="text-white hover:bg-white/10 min-h-[44px] px-2"
            data-testid="detail-logout-btn"
            title="Log out"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">
        {/* Details card */}
        <Card>
          <CardHeader><CardTitle className="text-[#004C97]">Submission Details</CardTitle></CardHeader>
          <CardContent className="p-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="detail-fields">
              {fields.map(([key, label]) => (
                <div key={key} className="min-w-0">
                  <dt className="text-[11px] uppercase tracking-wide text-gray-500">{label}</dt>
                  <dd className="text-sm text-gray-900 break-words">{renderValue(record[key])}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>

        {/* Star rating (jobs only) */}
        {kind === 'job' && (
          <Card>
            <CardHeader><CardTitle className="text-[#004C97] flex items-center gap-2"><Star className="w-4 h-4" /> Applicant Rating</CardTitle></CardHeader>
            <CardContent className="p-4">
              <div className="flex items-center gap-1" data-testid="detail-rating">
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = (record.rating || 0) >= n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => saveRating(n === (record.rating || 0) ? 0 : n)}
                      disabled={savingRating}
                      data-testid={`detail-rating-star-${n}`}
                      className="p-1"
                      aria-label={`Rate ${n} out of 5`}
                    >
                      <Star className={`w-6 h-6 ${filled ? 'fill-[#F9A825] text-[#F9A825]' : 'text-gray-300'}`} />
                    </button>
                  );
                })}
                <span className="ml-2 text-sm text-gray-600">{record.rating ? `${record.rating}/5` : 'Not rated'}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Click a star to set rating, click same star again to clear.</p>
            </CardContent>
          </Card>
        )}

        {/* Attachments (jobs only) */}
        {kind === 'job' && record.attachments && (
          <Card>
            <CardHeader><CardTitle className="text-[#004C97] flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Attachments</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-2" data-testid="detail-attachments">
              {['license', 'resume'].map((slot) => {
                const meta = record.attachments[slot];
                if (!meta) return null;
                return (
                  <div key={slot} className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {slot === 'license' ? "Driver's License" : 'Résumé'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {meta.original_filename} · {Math.round((meta.size_bytes || 0) / 1024)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => downloadAttachment(slot)}
                      disabled={downloading === slot}
                      className="bg-[#004C97] hover:bg-[#003a78] text-white"
                      data-testid={`detail-download-${slot}`}
                    >
                      {downloading === slot ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                      Download
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Status + notes editor */}
        <Card>
          <CardHeader><CardTitle className="text-[#004C97]">Status &amp; Internal Notes</CardTitle></CardHeader>
          <CardContent className="p-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-700" htmlFor="detail-status">Status</label>
              <select
                id="detail-status"
                data-testid="detail-status-select"
                value={draftStatus}
                onChange={(e) => setDraftStatus(e.target.value)}
                className="mt-1 w-full min-h-[44px] rounded-md border border-gray-300 px-3 text-sm"
              >
                {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {statusChanged && (
              <div>
                <label className="text-xs font-medium text-gray-700" htmlFor="detail-reason">Reason for status change (optional)</label>
                <textarea
                  id="detail-reason"
                  data-testid="detail-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
                  placeholder="Brief context for the audit log"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-700" htmlFor="detail-notes">Internal notes</label>
              <textarea
                id="detail-notes"
                data-testid="detail-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
                placeholder="Only visible to dispatch admins."
              />
            </div>
            <Button
              onClick={handleSave}
              disabled={!dirty || saving}
              data-testid="detail-save-btn"
              className="bg-[#F9A825] hover:bg-[#E08E1E] text-white disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save changes
            </Button>
          </CardContent>
        </Card>

        {/* Audit trail */}
        <Card>
          <CardHeader><CardTitle className="text-[#004C97] flex items-center gap-2"><History className="w-4 h-4" /> Audit Trail</CardTitle></CardHeader>
          <CardContent className="p-4" data-testid="detail-audit">
            {audit.length === 0 ? (
              <p className="text-sm text-gray-500">No audit events recorded yet.</p>
            ) : (
              <ul className="space-y-2">
                {audit.map((a, idx) => (
                  <li key={idx} className="rounded-md border border-gray-200 p-2 text-xs text-gray-700 flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span>
                      <strong>{a.action}</strong>
                      {a.status_from && a.status_to && ` — ${a.status_from} → ${a.status_to}`}
                      {a.reason && ` · ${a.reason}`}
                    </span>
                    <span className="text-gray-500">
                      {a.actor} · {a.timestamp_utc ? new Date(a.timestamp_utc).toLocaleString() : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DispatchInquiryDetail;
